import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Film, LogIn } from "lucide-react";
import { useTranslation } from "react-i18next";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import { useAuth } from "@/lib/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function Login() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from?.pathname || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success(t("toasts.welcome"));
      nav(from, { replace: true });
    } else {
      toast.error(res.error || "Credenciales inválidas");
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="marketing-shell min-h-screen">
      <MarketingNav />
      <section className="mx-auto max-w-lg px-6 py-16">
        <div className="glass-card rounded-3xl p-10">
          <p className="eyebrow text-gold">{t("nav.login")}</p>
          <h1 className="font-display text-4xl text-white mt-3">{t("auth.login_title")}</h1>
          <p className="text-white/70 mt-3">{t("auth.login_subtitle")}</p>

          <button
            onClick={google}
            className="mt-8 w-full inline-flex items-center justify-center gap-3 rounded-full bg-white text-slate-900 py-3 font-semibold hover:bg-white/90"
            data-testid="button-login-google"
          >
            <img alt="Google" src="https://www.gstatic.com/marketing-cms/assets/images/d5/dc/cfe9ce8b4425b410b49b7f2dd3f3/g.webp=s96-fcrop64=1,00000000ffffffff-rw" className="w-4 h-4" onError={(e) => (e.currentTarget.style.display = "none")} />
            {t("auth.or_google")}
          </button>
          <div className="my-6 flex items-center gap-3 text-white/40 text-xs">
            <div className="h-px bg-white/10 flex-1" /> {t("auth.email")} <div className="h-px bg-white/10 flex-1" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label className="text-white/80 text-sm">{t("auth.email")}</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required autoComplete="email"
                data-testid="input-login-email"
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-gold" />
            </div>
            <div>
              <Label className="text-white/80 text-sm">{t("auth.password")}</Label>
              <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required autoComplete="current-password"
                data-testid="input-login-password"
                className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-gold" />
            </div>
            <Button type="submit" disabled={loading} data-testid="button-login-submit" className="w-full gold-cta rounded-full py-6 text-base font-semibold">
              <LogIn size={16} className="mr-2" /> {loading ? t("common.loading") : t("auth.submit_login")}
            </Button>
          </form>

          <p className="mt-8 text-sm text-white/70 text-center">
            {t("auth.no_account")}{" "}
            <Link to="/registro" className="text-gold hover:underline" data-testid="link-to-register">
              {t("auth.create_free")}
            </Link>
          </p>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}
